import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, CheckCircle2, Circle, Lightbulb, ListChecks, Wand2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, Spinner } from "@/components/Common";
import { api, errMsg, humanize } from "@/lib/api";
import { findAts, findTailor, loadTools } from "@/lib/tools";
import { collectIdeas, hasContent } from "@/lib/tailor";
import { BoostContent } from "@/components/resume/BoostContent";
import { cn } from "@/lib/utils";

const KEY = "rpopro_rs_done";

const load = async () => {
  const d = await loadTools();
  const tools = [findTailor(d?.tools), findAts(d?.tools)].filter(Boolean);
  const res = await Promise.allSettled([api.get("/ai/resume-tips"), ...tools.map((t) => api.get(`/ai/tools/${t.id}/last`))]);
  const out = [];
  res.forEach((r) => r.status === "fulfilled" && collectIdeas(r.value.data, out));
  return [...new Set(out)].slice(0, 15);
};

const Preview = ({ p, onAccept, onDismiss, i, queue }) => (
  <div className="fade-up ml-6 mt-1 space-y-2 rounded-lg border border-[#D4AF37]/40 bg-[#09090B] p-3 text-xs" data-testid={`suggestion-preview-${i}`}>
    <div className="text-[10px] uppercase tracking-wider text-[#F3C649]">{humanize(p.section || "resume")} · proposed change{queue && ` · ${queue}`}</div>
    {p.explanation && <p className="text-zinc-400">{p.explanation}</p>}
    {p.before && <p className="break-words rounded bg-red-500/10 p-2 text-red-300 line-through decoration-red-400/60" data-testid={`suggestion-before-${i}`}>{p.before}</p>}
    {p.after && <p className="break-words rounded bg-emerald-500/10 p-2 text-emerald-300" data-testid={`suggestion-after-${i}`}>{p.after}</p>}
    <div className="flex gap-2">
      <Button size="sm" onClick={onAccept} data-testid={`suggestion-accept-${i}`}><Check className="h-4 w-4" />Accept</Button>
      <Button size="sm" variant="ghost" onClick={onDismiss} data-testid={`suggestion-dismiss-${i}`}><X className="h-4 w-4" />{queue ? "Skip" : "Dismiss"}</Button>
    </div>
  </div>
);

export const Suggestions = ({ resume, jobId, onApply, onUpdate, jobIdeas = [], source, content }) => {
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null);
  const [general, setGeneral] = useState(null);
  const [review, setReview] = useState(null);
  const [done, setDone] = useState(() => { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; } });
  const latest = useRef(resume);
  const reviewing = useRef(false);
  useEffect(() => { reviewing.current = !!review; }, [review]);
  latest.current = resume;
  useEffect(() => { load().then(setGeneral).catch(() => setGeneral([])); }, []);
  const items = general == null && !jobIdeas.length ? null : [...new Set([...jobIdeas, ...(general || [])])];
  const isJob = (s) => jobIdeas.includes(s);
  const mark = (s, v) => setDone((d) => { const n = { ...d, [s]: v }; localStorage.setItem(KEY, JSON.stringify(n)); return n; });
  const left = (items || []).filter((s) => !done[s]);

  const apply = async (s, i, rv = null) => {
    setBusy(i); setPreview(null);
    try {
      const { data } = await api.post("/resume-builder/apply-suggestion", { suggestion: s, resume: latest.current, job_id: jobId });
      if (!data?.resume) throw new Error(data?.explanation || "The AI couldn't find a clear change for this one. Try editing it by hand.");
      if (rv && !reviewing.current) return;
      setPreview({ ...data, s, i });
    } catch (e) {
      toast.error(e.response ? errMsg(e) : e.message);
      if (rv && reviewing.current) next(rv, s);
    } finally { setBusy(null); }
  };
  const next = (rv, skip) => {
    const seen = skip ? [...rv.seen, skip] : rv.seen;
    const n = items.findIndex((s) => !done[s] && !seen.includes(s));
    if (n < 0) { reviewing.current = false; setReview(null); setPreview(null); toast.success("Review finished"); return; }
    const r = { seen, total: rv.total };
    reviewing.current = true;
    setReview(r);
    apply(items[n], n, r);
  };
  const startReview = () => next({ seen: [], total: left.length });
  const accept = () => {
    onApply(preview.resume);
    latest.current = preview.resume;
    mark(preview.s, true);
    toast.success(`Applied to ${humanize(preview.section || "your resume")}`);
    const s = preview.s;
    setPreview(null);
    if (review && reviewing.current) setTimeout(() => reviewing.current && next(review, s), 0);
  };
  const dismiss = () => { const s = preview.s; setPreview(null); if (review && reviewing.current) next(review, s); };
  const stop = () => { reviewing.current = false; setReview(null); setPreview(null); };
  const pos = review && `${review.seen.length + 1} of ${review.total}`;

  return (
    <Card className="border-[#D4AF37]/30" data-testid="suggestions-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2 font-display text-base text-zinc-100 md:text-lg"><Lightbulb className="h-4 w-4 text-[#D4AF37]" />Optimization ideas</span>
        <div className="flex items-center gap-2">
          {items?.length > 0 && <span className="rounded-full bg-[#D4AF37]/15 px-2 py-0.5 font-mono text-[11px] text-[#F3C649]" data-testid="suggestions-left">{left.length} to do</span>}
          {review ? <Button size="sm" variant="ghost" onClick={stop} data-testid="suggestions-stop-review">Stop review</Button>
            : left.length > 1 && (<Button size="sm" variant="outline" onClick={startReview} disabled={busy != null} className="border-[#D4AF37]/50 text-[#F3C649]" data-testid="suggestions-apply-all"><ListChecks className="h-4 w-4" />Apply all</Button>)}
        </div>
      </div>
      {source && jobIdeas.length > 0 && <p className="mt-1 text-[11px] text-zinc-500">Ideas marked <span className="text-[#F3C649]">For this job</span> come from {source}.</p>}
      <div className="mt-3 max-h-96 space-y-1.5 overflow-y-auto pr-1">
        {items == null ? <p className="text-sm text-zinc-500">Loading your latest Boost and ATS feedback…</p>
          : !items.length ? hasContent(content) ? null : <p className="text-sm text-zinc-500" data-testid="suggestions-empty">No ideas yet. Run "Boost my score" or the ATS Pass-Rate check on a job, then tap "Tailor your resume".</p>
          : items.map((s, i) => (
            <div key={s}>
              <div className={cn("flex items-start gap-1 rounded-lg transition-colors hover:bg-white/5", preview?.i === i && "bg-white/5")}>
                <button type="button" onClick={() => mark(s, !done[s])} className="flex min-w-0 flex-1 items-start gap-2 p-2 text-left text-sm" data-testid={`suggestion-${i}`}>
                  {done[s] ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /> : <Circle className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500" />}
                  <span className={cn("break-words", done[s] ? "text-zinc-500 line-through" : "text-zinc-300")}>
                    {isJob(s) && <span className="mr-1.5 rounded-full bg-[#D4AF37]/15 px-1.5 py-0.5 align-middle text-[10px] font-medium text-[#F3C649]" data-testid={`suggestion-job-tag-${i}`}>For this job</span>}{s}
                  </span>
                </button>
                {!done[s] && <Button size="sm" variant="ghost" onClick={() => apply(s, i)} disabled={busy != null || !!review} className="mt-1 shrink-0 text-[#F3C649]" data-testid={`suggestion-apply-${i}`}>{busy === i ? <Spinner /> : <Wand2 className="h-3.5 w-3.5" />}Apply</Button>}
              </div>
              {preview?.i === i && <Preview p={preview} i={i} onAccept={accept} onDismiss={dismiss} queue={pos} />}
            </div>
          ))}
      </div>
      <BoostContent content={content} resume={resume} onUpdate={onUpdate} source={source} jobId={jobId} />
    </Card>
  );
};
